import { TextBox, TextBoxChangeEvent, TextBoxProps } from '@progress/kendo-react-inputs';
import { FC, FocusEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { stopPropagation } from 'src/utils';

interface NumberInputProps extends Omit<TextBoxProps, 'prefix'> {
  prefix?: string;
  postfix?: string;
  onChange: (e: TextBoxChangeEvent) => void;
  onBlur?: (e: any) => void;
  value: string;
  valid?: boolean;
  width?: number | string;
  decimal?: number;
}

const NumberInput: FC<NumberInputProps> = ({
  prefix = '',
  postfix = '',
  onChange,
  value,
  onBlur,
  valid = true,
  width = 'auto',
  decimal = 2,
  ...other
}) => {
  const id = useId();
  const [isInputFocused, setIsInputFocused] = useState(false);
  const inputRef = useRef<any>(null);

  const handleOnWheel = useCallback(
    (e: Event) => {
      if (isInputFocused) {
        e.preventDefault();
      }
    },
    [isInputFocused]
  );

  useEffect(() => {
    const ref = inputRef.current;
    ref?.element?.addEventListener('wheel', handleOnWheel);

    return () => ref?.element?.removeEventListener('wheel', handleOnWheel);
  }, [handleOnWheel]);

  const handleInputFocus = useCallback(
    (event: FocusEvent<HTMLInputElement, Element>) => {
      setIsInputFocused(true);
      if (other.onFocus) {
        other.onFocus(event);
      }
    },
    [other]
  );

  const handleInputBlur = useCallback(
    (e: any) => {
      onBlur?.(e);
      setIsInputFocused(false);
    },
    [onBlur]
  );
  const modifyValue = useMemo(
    () =>
      isInputFocused
        ? Number(parseFloat(value))
          ? Number(parseFloat(value).toFixed(decimal))
          : value
        : value === ''
        ? ''
        : Number(value) < 0
        ? `-${prefix}${Math.abs(Number(value || 0)).toFixed(decimal)}${postfix}`
        : `${prefix}${Number(value || 0).toFixed(decimal)}${postfix}`,
    [decimal, isInputFocused, postfix, prefix, value]
  );

  const preventDefault = useCallback((e: any) => {
    e.preventDefault();
  }, []);

  useEffect(() => {
    if (isInputFocused) {
      inputRef.current?.element?.select();
    }
  }, [isInputFocused]);

  const handleChange = useCallback(
    (e: TextBoxChangeEvent) => {
      const { value } = e;
      const isExceededDecimalPointLength = value
        ? value?.toString().split('.')[1]?.length > decimal
        : false;
      if (isExceededDecimalPointLength) {
        return;
      }
      onChange(e);
    },
    [onChange, decimal]
  );

  return (
    <span
      style={{ width }}
      onDragStart={preventDefault}
      onDragOver={preventDefault}
      onClick={stopPropagation}
    >
      <TextBox
        {...other}
        ref={inputRef}
        style={{ width: width }}
        type={isInputFocused ? 'number' : 'text'}
        value={modifyValue}
        onChange={handleChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        valid={typeof valid === 'boolean' ? valid : value?.length !== 0 && !isNaN(Number(value))}
        size="small"
        id={id}
      />
    </span>
  );
};

export default NumberInput;
